import { bunja } from 'bunja';
import { atom } from 'jotai';
import { Actor, Subscription, createActor } from 'xstate';

import { MfaScope, MfaVerifyChannel } from '@flipster/apis/v1/mfa/types';
import { overlayBunja } from '@flipster/overlay';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

import MfaVerifyFlowModal from './MfaVerifyFlowModal';
import { emailOtpBunja } from './_form/EmailOtpInput';
import { smsOtpBunja } from './_form/SmsOtpInput';
import { totpOtpBunja } from './_form/TotpOtpInput';
import mfaVerifyMachine from './machine';

const passkeyUnavailableScope: MfaScope[] = ['RESET_PASSWORD'];

const MfaVerifyFlowModalId = 'MfaVerifyFlowModal';

interface Options {
  email?: string;
  phoneNumber?: string;
  passwordResetToken?: string;
  authToken?: string;
  withdrawalRequestId?: string;
  silent?: boolean; // Modal을 띄우지 않고 진행할 때 사용
  /**
   * Machine state가 DONE으로 final 상태일 때
   * @param mfaSessionToken
   */
  onDone?: (mfaSessionToken: string) => unknown;
  /**
   * Verify flow가 완료되어 Confirm 버튼을 눌렀을 때
   * @param mfaSessionToken
   */
  onConfirm?: (mfaSessionToken: string) => unknown;
}

export const mfaVerifyFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlay = bunja.use(overlayBunja);

  const mfaVerifyActorAtom = atom<Actor<typeof mfaVerifyMachine>>(createActor(mfaVerifyMachine));
  const subscriptionAtom = atom<Subscription>({} as Subscription);

  function createMfaVerifyActor() {
    const actor = createActor(mfaVerifyMachine);
    store.set(mfaVerifyActorAtom, actor);
    return store.get(mfaVerifyActorAtom);
  }

  function destroy() {
    overlay.close(MfaVerifyFlowModalId);
    store.get(subscriptionAtom)?.unsubscribe?.();
    store.get(mfaVerifyActorAtom)?.stop();
  }

  function startVerify(mfaScope: MfaScope, options: Options) {
    const mfaVerifyActor = createMfaVerifyActor();

    if (!mfaVerifyActor) return;

    const passkeyAvailable = !passkeyUnavailableScope.includes(mfaScope);
    mfaVerifyActor.start().send({
      type: 'initiate',
      mfaScope,
      passkeyAvailable,
      authToken: options?.authToken,
      email: options?.email,
      maskedPhoneNumber: options?.phoneNumber,
      passwordResetToken: options?.passwordResetToken,
      withdrawalRequestId: options?.withdrawalRequestId,
    });

    if (!options.silent)
      overlay.open(
        () => (
          <MfaVerifyFlowModal
            onConfirm={async (mfaSessionToken) => {
              try {
                await options?.onConfirm?.(mfaSessionToken);
              } finally {
                destroy();
              }
            }}
          />
        ),
        MfaVerifyFlowModalId,
      );

    store.set(
      subscriptionAtom,
      mfaVerifyActor.subscribe({
        complete: () => {
          const { output, value } = mfaVerifyActor.getSnapshot();
          if (value === 'NO_REQUIRED' && output) options?.onConfirm?.(output.mfaSessionToken);
          else if (output) options?.onDone?.(output.mfaSessionToken);
        },
        error: (error) => {
          snackbar.error({ message: getErrorMessage(error) });
          overlay.close(MfaVerifyFlowModalId);
        },
      }),
    );
  }

  bunja.effect(() => {
    return () => {
      destroy();
    };
  });

  return {
    mfaVerifyActorAtom,
    startVerify,
    destroy,
  };
});

mfaVerifyFlowBunja.debugLabel = 'mfaVerifyFlowBunja';

export const mfaVerifyFormBunja = bunja(() => {
  const form = {
    email: bunja.use(emailOtpBunja),
    sms: bunja.use(smsOtpBunja),
    totp: bunja.use(totpOtpBunja),
  };

  const setError = (channel: MfaVerifyChannel, error: string) => {
    form[channel].setError(error);
  };

  const resetField = (channel: MfaVerifyChannel) => {
    form[channel].clear();
  };

  return {
    setError,
    resetField,
  };
});

mfaVerifyFormBunja.debugLabel = 'mfaVerifyFormBunja';
